import {
  CalendarMonthOutlined,
  LinkOutlined,
  MoreVertRounded,
  PersonOutlineRounded,
  ThumbUp,
  ThumbUpOutlined,
  Tonality,
} from "@mui/icons-material";
import { Box, IconButton, Link, Menu, MenuItem, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { DateTime } from "luxon";
import React, { useMemo, useState } from "react";
import {
  AvatarGroup,
  BorderlessTextField,
  ContextLabel,
  IssueDrawerContent,
  IssueStatusPicker,
  ItemDrawerTitle,
  Table,
  TableDrawerButton,
  useDrawer,
} from "src/components";
import { DEFAULT_ISSUE_STATUS, Links } from "src/constants";
import { useSession } from "src/hooks";
import { IssueSummaryDto, useDeleteIssue, useEditIssue, useRemoveIssueUpvote, useUpvoteIssue } from "src/http";
import { useDestructiveConfirm } from "src/utilities";

const columnHelper = createColumnHelper<IssueSummaryDto>();

const columnsConfig = (
  handleIssueBlurred: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
    issue: IssueSummaryDto
  ) => void,
  handleShowDrawerClicked: (e: React.MouseEvent, issue: IssueSummaryDto) => void,
  handleUpvoteToggled: (issue: IssueSummaryDto) => void,
  handleActionsClicked: (e: React.MouseEvent<HTMLElement>, issue: IssueSummaryDto) => void,
  sessionUserId: string
) => {
  return [
    columnHelper.accessor("description", {
      id: "Name",
      enableHiding: false,
      size: 400,
      header: () => (
        <Box sx={{ textAlign: "left" }}>
          <Typography variant="caption">NAME</Typography>
        </Box>
      ),
      cell: (props) => (
        <Stack
          direction="row"
          sx={{
            width: "100%",
            height: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            overflow: "hidden",
          }}
        >
          <BorderlessTextField
            value={props.row.original.description}
            onClick={(e) => props.row.original.userCanEditTitle && e.stopPropagation()}
            onBlur={(e) => handleIssueBlurred(e, props.row.original)}
            placeholder="Enter item..."
            isEditable={props.row.original.userCanEditTitle}
            maxLength={512}
          />
          <TableDrawerButton onClick={(e) => handleShowDrawerClicked(e, props.row.original)} />
        </Stack>
      ),
    }),
    columnHelper.accessor("currentStatus", {
      id: "Status",
      size: 200,
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <Tonality color="disabled" />
          <Typography variant="caption">STATUS</Typography>
        </Stack>
      ),
      cell: (props) => (
        <IssueStatusPicker
          variant="select"
          issueGuid={props.row.original.guid}
          status={props.row.original.currentStatus ?? DEFAULT_ISSUE_STATUS}
          disabled={!props.row.original.userCanEditOtherProperties}
          teamSlug={props.row.original.assignedTeam?.slug}
          createdByUserGuid={props.row.original.createdByUser.userId}
        />
      ),
    }),
    columnHelper.accessor("upvotes", {
      id: "Upvotes",
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <ThumbUpOutlined color="disabled" />
          <Typography variant="caption">UPVOTES</Typography>
        </Stack>
      ),
      cell: (props) => (
        <Tooltip
          title={
            props.row.original.upvotes.length > 0
              ? props.row.original.upvotes
                  .sort((a, b) => a.displayName.localeCompare(b.displayName))
                  .map((u, i) => [
                    <Typography key={i} sx={{ display: "inline-block", fontSize: 13, marginRight: "2px" }}>
                      <Link href={Links.User(u.userId)}>{u.displayName}</Link>
                      {i < props.row.original.upvotes.length - 1 && ", "}
                    </Typography>,
                  ])
              : null
          }
        >
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            <IconButton onClick={() => handleUpvoteToggled(props.row.original)}>
              {props.row.original.upvotes.some((x) => x.userId === sessionUserId) ? <ThumbUp /> : <ThumbUpOutlined />}
            </IconButton>
            <Typography>{props.row.original.upvotes.length} votes</Typography>
          </Stack>
        </Tooltip>
      ),
    }),
    columnHelper.accessor("context", {
      id: "Context",
      size: 300,
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <LinkOutlined color="disabled" />
          <Typography variant="caption">CONTEXT</Typography>
        </Stack>
      ),
      cell: (props) => {
        if (!props.row.original.context) {
          return "";
        }

        return <ContextLabel context={props.row.original.context} hideTeamName />;
      },
    }),
    columnHelper.accessor("createdByUser", {
      id: "Creator",
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <PersonOutlineRounded color="disabled" />
          <Typography variant="caption">CREATOR</Typography>
        </Stack>
      ),
      cell: (props) => (
        <AvatarGroup max={1} users={[props.row.original.createdByUser]} avatarProps={{ size: "xsmall" }} />
      ),
    }),
    columnHelper.accessor("createdDateTimeUtc", {
      id: "Created On",
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <CalendarMonthOutlined color="disabled" />
          <Typography variant="caption">CREATED ON</Typography>
        </Stack>
      ),
      cell: (props) => (
        <Typography noWrap>
          {DateTime.fromISO(props.row.original.createdDateTimeUtc, { zone: "utc" })
            .toLocal()
            .toLocaleString(DateTime.DATE_MED)}
        </Typography>
      ),
    }),
    columnHelper.display({
      id: "Actions",
      size: 50,
      minSize: 50,
      enableResizing: false,
      enableHiding: false,
      meta: {
        hideColumnMenu: true,
        hideResizer: true,
      },
      cell: (props) => {
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mx: 0.25 }}>
            <IconButton size="small" onClick={(e) => handleActionsClicked(e, props.row.original)}>
              <MoreVertRounded />
            </IconButton>
          </Box>
        );
      },
    }),
  ];
};

interface Props {
  tableId: string;
  issues: IssueSummaryDto[];
  enableActions?: boolean;
}

const IssuesTable = (props: Props) => {
  const sessionUser = useSession();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedItem, setSelectedItem] = useState<IssueSummaryDto>();
  const { mutate: editIssue } = useEditIssue();
  const { mutate: upvoteIssue } = useUpvoteIssue();
  const { mutate: removeIssueUpvote } = useRemoveIssueUpvote();
  const { mutate: deleteIssue } = useDeleteIssue();
  const { showDrawer } = useDrawer();
  const theme = useTheme();
  const confirm = useDestructiveConfirm();

  const handleIssueBlurred = useMemo(
    () => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, issue: IssueSummaryDto) => {
      if (issue.userCanEditTitle) {
        editIssue({
          issueGuid: issue.guid,
          description: e.target.value,
          createdByUserId: issue.createdByUser.userId,
          teamSlug: issue.assignedTeam?.slug,
        });
      }
    },
    [editIssue]
  );

  const handleShowDrawerClicked = useMemo(
    () => (e: React.MouseEvent, issue: IssueSummaryDto) => {
      e.stopPropagation();

      showDrawer({
        title: <ItemDrawerTitle link={Links.IssueDetail(issue.guid)} />,
        content: <IssueDrawerContent issueGuid={issue.guid} />,
      });
    },
    [showDrawer]
  );

  const handleUpvoteToggled = useMemo(
    () => (issue: IssueSummaryDto) => {
      if (issue.upvotes.some((x) => x.userId === sessionUser.userId)) {
        removeIssueUpvote({
          issueGuid: issue.guid,
          teamSlug: issue.assignedTeam?.slug,
          createdByUserId: issue.createdByUser.userId,
          userId: sessionUser.userId,
        });
      } else {
        upvoteIssue({
          issueGuid: issue.guid,
          teamSlug: issue.assignedTeam?.slug,
          createdByUserId: issue.createdByUser.userId,
          user: {
            userId: sessionUser.userId,
            displayName: sessionUser.displayName,
            enabled: true,
            profilePhotoSmallUrl: sessionUser.profilePhotoSmallUrl,
          },
        });
      }
    },
    [sessionUser.userId, sessionUser.displayName, sessionUser.profilePhotoSmallUrl, removeIssueUpvote, upvoteIssue]
  );

  function handleActionsClicked(e: React.MouseEvent<HTMLElement>, issue: IssueSummaryDto) {
    setMenuAnchor(e.currentTarget);
    setSelectedItem(issue);
    e.stopPropagation();
  }

  async function handleDeleteClicked() {
    setMenuAnchor(null);

    if (!selectedItem) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Delete Issue",
      content: "Are you sure you want to delete this issue?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteIssue({
      issueGuid: selectedItem.guid,
      createdByUserId: selectedItem.createdByUser.userId,
      teamSlug: selectedItem.assignedTeam?.slug,
    });
  }

  const columns = useMemo(
    () =>
      columnsConfig(
        handleIssueBlurred,
        handleShowDrawerClicked,
        handleUpvoteToggled,
        handleActionsClicked,
        sessionUser.userId
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleIssueBlurred, handleShowDrawerClicked, handleUpvoteToggled, sessionUser.userId, props.issues]
  );

  return (
    <>
      <Table
        tableId={props.tableId}
        data={props.issues}
        columns={columns}
        enableSorting={false}
        columnPinning={{
          right: ["Actions"],
        }}
        sx={{
          "table thead th": {
            backgroundColor: "#fef5ed",
          },
          "table thead tr th.pinned, table tbody tr td.pinned": {
            backgroundColor: "#fef5ed",
          },
          "table thead tr th.pinned-last-left, table tbody tr td.pinned-last-left": {
            background: "linear-gradient(90deg, #fef5ed 80%, transparent 100%)",
          },
          "table thead tr th.pinned-first-right, table tbody tr td.pinned-first-right": {
            background: "linear-gradient(90deg, transparent 0%, #fef5ed 20%)",
          },
          "table tbody tr:hover": {
            backgroundColor: "#fdecdd",
          },
          "table tbody tr:hover td.pinned": {
            background: "#fdecdd",
          },
          "table tbody tr:hover td.pinned-last-left": {
            background: "linear-gradient(90deg, #fdecdd 80%, transparent 100%)",
          },
          "table tbody tr:hover td.pinned-first-right": {
            background: "linear-gradient(90deg, transparent 0%, #fdecdd 20%)",
          },
        }}
      />

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={handleDeleteClicked} sx={{ color: theme.palette.error.main }}>
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

export { IssuesTable };
